import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Download, FileText, Loader2, Store, Pin, Printer, CheckCircle2, ExternalLink, AlertCircle, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { sizeConfigs } from './themes';
import axios from 'axios';
import { checkExportEntitlement } from '../../utils/exportEntitlement';
import CanvaHandoff from '../CanvaHandoff';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExportPanel = ({
  onExportPNG, onExportPDF, exporting, exportProgress,
  totalCount, outputSize, countLabel = 'pages',
  variant = 'document',
  productTitle = '', productDescription = '', productType = '',
  productSlug = null, projectId = null, themeId = 'modern-minimal',
}) => {
  const sizeLabel = outputSize && sizeConfigs[outputSize] ? ` (${sizeConfigs[outputSize].label})` : '';
  const isVisual = variant === 'visual';
  const [entitlement, setEntitlement] = useState(null);

  useEffect(() => {
    checkExportEntitlement(productSlug).then(setEntitlement);
  }, [productSlug]);

  // Shopify export state
  const [showShopifyDialog, setShowShopifyDialog] = useState(false);
  const [shopifyConnected, setShopifyConnected] = useState(null); // null = unknown
  const [shopifyExporting, setShopifyExporting] = useState(false);
  const [shopifyResult, setShopifyResult] = useState(null); // { success, message, url } | null
  const [shopifyError, setShopifyError] = useState('');
  const [exportTitle, setExportTitle] = useState('');
  const [exportPrice, setExportPrice] = useState('0.00');

  const handleShopifyClick = async () => {
    setShopifyResult(null);
    setShopifyError('');
    setExportTitle(productTitle || '');

    // Check connection status
    try {
      const res = await axios.get(`${API}/shopify/status`);
      setShopifyConnected(res.data.connected);
      if (!res.data.connected) {
        setShopifyError('not_connected');
      }
    } catch {
      setShopifyConnected(false);
      setShopifyError('not_connected');
    }
    setShowShopifyDialog(true);
  };

  const handleShopifyExport = async () => {
    if (!exportTitle.trim()) return;
    setShopifyExporting(true);
    setShopifyError('');
    setShopifyResult(null);

    try {
      const res = await axios.post(`${API}/shopify/export`, {
        title: exportTitle.trim(),
        description: productDescription || `<p>Created with Create by Lumera — ${productType}</p>`,
        product_type: productType || 'Digital Download',
        price: parseFloat(exportPrice) || 0,
        tags: ['lumera', productType?.toLowerCase().replace(/\s+/g, '-')].filter(Boolean),
      });

      setShopifyResult({
        success: true,
        message: res.data.message,
        url: res.data.shopify_url,
      });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Export failed. Try again.';
      setShopifyError(detail);
    }
    setShopifyExporting(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-6">
      <h3 className="font-sans font-bold text-stone-800 mb-4">Export</h3>
      <div className="space-y-3">
        <Button
          data-testid={isVisual ? 'export-png-btn' : 'doc-export-png-btn'}
          onClick={onExportPNG}
          disabled={exporting}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl py-5 font-sans font-semibold"
        >
          {exporting
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting {exportProgress}%</>
            : <><Download className="w-4 h-4 mr-2" /> Download PNG</>}
        </Button>
        <Button
          data-testid={isVisual ? 'export-pdf-btn' : 'doc-export-pdf-btn'}
          onClick={onExportPDF}
          disabled={exporting}
          className="w-full bg-stone-900 text-white hover:bg-stone-800 rounded-xl py-5 font-sans font-semibold"
        >
          {exporting
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting {exportProgress}%</>
            : <>{isVisual ? <FileText className="w-4 h-4 mr-2" /> : <Printer className="w-4 h-4 mr-2" />} Download {isVisual ? 'PDF' : 'print-ready PDF'}</>}
        </Button>
      </div>
      <p className="text-xs font-sans text-stone-400 mt-3 text-center">
        {isVisual ? `All slides` : `${totalCount} ${countLabel}`}{sizeLabel}
      </p>

      {/* Export entitlement info */}
      {entitlement && !entitlement.clean && (
        <div data-testid="export-branding-notice" className="flex items-start gap-2 bg-stone-50 border border-stone-100 rounded-lg p-2.5 mt-3">
          <Info className="w-3.5 h-3.5 text-stone-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[11px] text-stone-500">Free export includes a small <span className="font-semibold">Created with Lumera</span> footer.</p>
            <p className="text-[10px] text-stone-400 mt-0.5">Purchase this product or upgrade to Pro for a clean, unbranded export.</p>
          </div>
        </div>
      )}
      {entitlement && entitlement.clean && (
        <div data-testid="export-clean-notice" className="flex items-center gap-1.5 mt-2 justify-center">
          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
          <p className="text-[10px] text-emerald-600 font-medium">Clean export — no branding</p>
        </div>
      )}

      {isVisual && (
        <div className="border-t border-stone-100 mt-4 pt-4">
          <Button disabled variant="outline" className="w-full rounded-xl py-5 font-sans font-semibold border-stone-200 text-stone-400 cursor-not-allowed relative">
            <Pin className="w-4 h-4 mr-2" /> Pinterest Pins
            <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">Coming soon</span>
          </Button>
          <p className="text-xs font-sans text-stone-400 mt-2 text-center">Auto-generate marketing pins</p>
        </div>
      )}

      {/* Canva Handoff */}
      <CanvaHandoff productSlug={productSlug} productId={projectId} themeId={themeId} />

      {/* Shopify Export — Coming Soon */}
      <div className="border-t border-stone-100 mt-4 pt-4">
        <div
          data-testid={isVisual ? 'shopify-export-btn' : 'doc-shopify-btn'}
          className="w-full rounded-xl py-4 font-sans text-sm border border-stone-200 text-stone-400 text-center flex items-center justify-center gap-2 cursor-default opacity-70"
        >
          <Store className="w-4 h-4" /> Sell on Shopify — Coming soon
        </div>
      </div>

      {/* Shopify Export Dialog */}
      <Dialog open={showShopifyDialog} onOpenChange={(open) => { if (!shopifyExporting) setShowShopifyDialog(open); }}>
        <DialogContent className="sm:max-w-md bg-white rounded-2xl border-stone-100 p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="shopify-export-dialog">
          <div className="bg-[#95BF47] px-6 py-5">
            <DialogHeader>
              <DialogTitle className="text-white font-serif text-xl flex items-center gap-2">
                <Store className="w-5 h-5" /> Export to Shopify
              </DialogTitle>
              <DialogDescription className="text-white/80 font-sans text-sm mt-1">
                Product will be created as draft in your store
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-6 space-y-4">
            {/* Not connected state */}
            {shopifyError === 'not_connected' && (
              <div className="text-center py-4" data-testid="shopify-not-connected">
                <div className="w-14 h-14 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
                  <Store className="w-7 h-7 text-stone-400" />
                </div>
                <p className="font-sans font-semibold text-stone-800 text-sm mb-1">No store connected</p>
                <p className="font-sans text-stone-500 text-xs mb-4">Connect your Shopify store first under Profile → Integrations.</p>
                <Button
                  data-testid="go-to-integrations-btn"
                  onClick={() => { setShowShopifyDialog(false); window.location.href = '/profile'; }}
                  className="bg-[#95BF47] hover:bg-[#7ea03a] text-white rounded-full px-6 h-10 text-xs font-sans font-semibold"
                >
                  Go to Integrations
                </Button>
              </div>
            )}

            {/* Connected — export form */}
            {shopifyConnected && shopifyError !== 'not_connected' && !shopifyResult && (
              <div className="space-y-4" data-testid="shopify-export-form">
                <div>
                  <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Product title</label>
                  <input
                    data-testid="shopify-export-title"
                    type="text"
                    value={exportTitle}
                    onChange={(e) => setExportTitle(e.target.value)}
                    placeholder="Enter product title"
                    className="w-full px-3 py-2.5 border border-stone-200 rounded-xl font-sans text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-[#95BF47] transition-colors"
                  />
                </div>
                <div>
                  <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Price (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 font-sans text-sm">$</span>
                    <input
                      data-testid="shopify-export-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={exportPrice}
                      onChange={(e) => setExportPrice(e.target.value)}
                      className="w-full pl-7 pr-3 py-2.5 border border-stone-200 rounded-xl font-sans text-sm text-stone-700 focus:outline-none focus:border-[#95BF47] transition-colors"
                    />
                  </div>
                </div>

                {shopifyError && shopifyError !== 'not_connected' && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="shopify-export-error">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="font-sans text-xs text-red-700">{shopifyError}</p>
                  </div>
                )}

                <Button
                  data-testid="shopify-confirm-export-btn"
                  onClick={handleShopifyExport}
                  disabled={shopifyExporting || !exportTitle.trim()}
                  className="w-full bg-[#95BF47] hover:bg-[#7ea03a] text-white rounded-xl py-5 font-sans font-semibold"
                >
                  {shopifyExporting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting...</>
                  ) : (
                    <><Store className="w-4 h-4 mr-2" /> Create in Shopify</>
                  )}
                </Button>
              </div>
            )}

            {/* Success state */}
            {shopifyResult?.success && (
              <div className="text-center py-4" data-testid="shopify-export-success">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-emerald-500" />
                </div>
                <p className="font-sans font-semibold text-stone-800 text-sm mb-1">Exported!</p>
                <p className="font-sans text-stone-500 text-xs mb-4">{shopifyResult.message}</p>
                {shopifyResult.url && (
                  <a
                    href={shopifyResult.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="shopify-view-product-link"
                    className="inline-flex items-center gap-1.5 text-xs font-sans font-semibold text-[#95BF47] hover:underline"
                  >
                    Se i Shopify <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                <div className="mt-4">
                  <Button
                    onClick={() => setShowShopifyDialog(false)}
                    variant="outline"
                    className="rounded-full px-6 h-9 text-xs font-sans font-medium border-stone-200"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExportPanel;
